import type { Key } from 'react';

export type PatientSearchSortOrder = 'asc' | 'desc';

type AntSortOrder = 'ascend' | 'descend' | null | undefined;

export type PatientSearchSorter = {
  columnKey?: Key;
  field?: Key | readonly Key[];
  order?: AntSortOrder;
};

export type PatientSearchSort = {
  sortField: string;
  sortOrder: PatientSearchSortOrder;
};

export const PATIENT_SEARCH_TABLE_PANEL_CLASS =
  'flex h-[50vh] min-h-[360px] flex-col overflow-hidden';

const DEFAULT_PATIENT_SEARCH_SORT: PatientSearchSort = {
  sortField: 'name_kana',
  sortOrder: 'asc',
};

const SORT_FIELD_MAPPING: Record<string, string> = {
  serial_number: 'serial_number',
  name: 'name',
  name_kana: 'name_kana',
  address1: 'address1',
  insurance_symbol: 'card_mark',
  insurance_number: 'card_number',
  official: 'official',
};

export function resolvePatientSearchSort(
  sorter: PatientSearchSorter,
  currentSort?: PatientSearchSort
): PatientSearchSort {
  if (!sorter.order) {
    if (currentSort) {
      return {
        sortField: currentSort.sortField,
        sortOrder: currentSort.sortOrder === 'asc' ? 'desc' : 'asc',
      };
    }

    return DEFAULT_PATIENT_SEARCH_SORT;
  }

  const fieldKey = sorter.columnKey ?? sorter.field;
  const frontendSortField = Array.isArray(fieldKey) ? String(fieldKey[0] ?? '') : String(fieldKey ?? '');
  const sortField = SORT_FIELD_MAPPING[frontendSortField] || frontendSortField;

  return {
    sortField: sortField || DEFAULT_PATIENT_SEARCH_SORT.sortField,
    sortOrder: sorter.order === 'descend' ? 'desc' : 'asc',
  };
}

export function getPatientSearchColumnSortOrder(
  columnKey: string,
  currentSort: PatientSearchSort
): 'ascend' | 'descend' | undefined {
  const backendField = SORT_FIELD_MAPPING[columnKey] || columnKey;
  if (backendField !== currentSort.sortField) return undefined;

  return currentSort.sortOrder === 'desc' ? 'descend' : 'ascend';
}

export function getPatientSearchTableInstanceKey(
  sortField: string,
  sortOrder: PatientSearchSortOrder
): string {
  return `patient-search-${sortField}-${sortOrder}`;
}
